/**
 * KAPITEL-PUNKTE — senkrechte Punkte-Leiste rechts mittig (Jans Wunsch
 * 06.10.2026, Vorbild: die Leiste aus Scrolly 1.0 — dort waagerecht unten).
 *
 * Ein großer Punkt je Kapitel; die Slides des aktuellen Kapitels fließen als
 * kleine Punkte aus ihm heraus (und beim Weiterlesen wieder hinein). Wo der
 * Leser steht, zeigt ein größerer Tropfen, der von Punkt zu Punkt „schlonzt“:
 * Der Kopf eilt voraus, der Tropfen zieht sich in die Länge, hängt kurz am
 * alten Punkt und reißt ab (Metaball-Hälse, metaball.ts). Ein Klick springt
 * zum Kapitel bzw. zur Slide. Ohne Kapitel: ein Punkt je Slide.
 * Erscheint genau dann, wenn auch die Nav-Knöpfe da sind, in deren Farben.
 * Welche Punkte es gibt und wo sie sitzen, steht in kapitelGruppen.ts.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type VisualStoryStore from "./VisualStoryStore";
import { kapitelGruppen, kapitelName, kapitelPosition, passendeMasse, punktLagen } from "./kapitelGruppen";
import type { KapitelSlide, PunktLage, PunktMasse } from "./kapitelGruppen";
import { hals } from "./metaball";
import type { Kreis } from "./metaball";
import { useStoryPhase } from "./useStoryPhase";

/** Mitte der Nav-Knopf-Spalte vom rechten Rand (Knöpfe 48 px, Abstand 12 px). */
const MITTE_VON_RECHTS = 12 + 48 / 2;
/** Deckkraft der Punkte, auf denen der Leser gerade nicht steht */
const BLASS = 0.6;
/** Hälse reißen bei diesem Anteil des Punktabstands — in Ruhe berührt sich nichts */
const REICHWEITE = 0.6;
/** Deckkraft der Pille in der Grundfarbe der Nav-Knöpfe (ohne Weichzeichner etwas kräftiger) */
const PILLE = 0.4;
/** Platz über und unter der Pille im SVG, falls der Tropfen beim Federn übersteht */
const UEBERSTAND = 16;

/** Gedämpfte Feder: Wert, Tempo, Ziel. */
interface Feder {
    x: number;
    v: number;
    ziel: number;
}
const feder = (x: number): Feder => ({ x, v: 0, ziel: x });

/**
 * Steifigkeit k und Dämpfung d: Punkte und Leiste gleiten, der Kopf des
 * Tropfens eilt leicht federnd voraus, der Schwanz hängt nach.
 */
const LAGE = { k: 220, d: 26 };
const KOPF = { k: 320, d: 24 };
const SCHWANZ = { k: 130, d: 20 };

/** Ein Zeitschritt; true, wenn die Feder ruht. */
function schritt(f: Feder, dt: number, art: { k: number; d: number }): boolean {
    f.v += (-art.k * (f.x - f.ziel) - art.d * f.v) * dt;
    f.x += f.v * dt;
    if (Math.abs(f.x - f.ziel) < 0.01 && Math.abs(f.v) < 0.05) {
        f.x = f.ziel;
        f.v = 0;
        return true;
    }
    return false;
}

/**
 * Wechselt das Kapitel, schlonzt der Tropfen erst zum nächsten Punkt, dann
 * ordnet sich die Leiste um und trägt ihn mit — sonst flösse er beim
 * Vorwärtslesen nach oben (das neue Kapitel rückt ja hoch).
 */
const UMBAU_VERZUG = 180;

interface Bewegung {
    /** Die Lagen, auf die die Federn gerade zielen (beim Umbau kurz die alten) */
    lagen: { punkte: PunktLage[]; hoehe: number };
    hoehe: Feder;
    punkte: Map<number, { y: Feder; r: Feder }>;
    /** Der Tropfen: Kopf und Schwanz (in Ruhe an derselben Stelle) und sein Radius */
    kopf: Feder;
    schwanz: Feder;
    tropfen: Feder;
}

const ohneBewegung = () =>
    typeof window === "undefined" ||
    typeof window.requestAnimationFrame !== "function" ||
    !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Bewegt Leiste, Punkte und Tropfen per Federn zu ihren Zielen und zeichnet
 * dabei jedes Bild neu; hört auf, sobald alles ruht. Beim ersten Zeichnen
 * steht alles schon am Ziel.
 */
function useSchlonzen(
    ziel: { punkte: PunktLage[]; hoehe: number } | null,
    aktiv: PunktLage | null,
    masse: PunktMasse | null,
): Bewegung | null {
    const zustand = useRef<Bewegung | null>(null);
    const [, neuZeichnen] = useState(0);
    if (ziel && masse && !zustand.current) {
        const y = aktiv?.y ?? 0;
        zustand.current = {
            lagen: ziel,
            hoehe: feder(ziel.hoehe),
            punkte: new Map(ziel.punkte.map((p) => [p.slide, { y: feder(p.y), r: feder(p.r) }])),
            kopf: feder(y),
            schwanz: feder(y),
            tropfen: feder(aktiv ? aktiv.r + masse.zuwachs : 0),
        };
    }

    useEffect(() => {
        const z = zustand.current;
        if (!z || !ziel || !masse) return;
        const umbauen = () => {
            z.lagen = ziel;
            z.hoehe.ziel = ziel.hoehe;
            const punkte = new Map<number, { y: Feder; r: Feder }>();
            for (const p of ziel.punkte) {
                const f = z.punkte.get(p.slide) ?? { y: feder(p.y), r: feder(p.r) };
                f.y.ziel = p.y;
                f.r.ziel = p.r;
                punkte.set(p.slide, f);
            }
            z.punkte = punkte;
        };
        // Der Tropfen jagt dem Punkt nach, auf dem der Leser steht — auch
        // während der Punkt selbst noch gleitet
        const tropfenZiel = () => {
            if (!aktiv) return;
            const y = z.punkte.get(aktiv.slide)?.y.x ?? aktiv.y;
            z.kopf.ziel = y;
            z.schwanz.ziel = y;
        };
        const sichtbar = z.tropfen.x >= 0.5;
        const sofort = ohneBewegung();
        // Ohne sichtbaren Tropfen gibt es nichts abzuwarten
        if (z.lagen !== ziel && (sofort || !sichtbar || !aktiv)) umbauen();
        if (aktiv) {
            // Ein unsichtbarer Tropfen fließt nicht von irgendwoher — er wächst am Ziel
            if (!sichtbar) {
                const y = z.punkte.get(aktiv.slide)?.y.x ?? aktiv.y;
                z.kopf = feder(y);
                z.schwanz = feder(y);
            }
            z.tropfen.ziel = aktiv.r + masse.zuwachs;
        } else {
            z.tropfen.ziel = 0;
        }

        const federn = () => [z.hoehe, z.tropfen, ...[...z.punkte.values()].flatMap((f) => [f.y, f.r])];
        if (sofort) {
            for (const f of federn()) {
                f.x = f.ziel;
                f.v = 0;
            }
            tropfenZiel();
            for (const f of [z.kopf, z.schwanz]) {
                f.x = f.ziel;
                f.v = 0;
            }
            neuZeichnen((n) => n + 1);
            return;
        }
        // Der Schwanz hängt höchstens einen Kapitelabstand nach
        const maxZug = masse.gross + masse.abstand;
        let umbauUm: number | null = null;
        let id = 0;
        let vorher: number | null = null;
        const takt = (jetzt: number) => {
            if (z.lagen !== ziel) {
                umbauUm ??= jetzt + UMBAU_VERZUG;
                if (jetzt >= umbauUm) umbauen();
            }
            let dt = vorher === null ? 1 / 60 : Math.min(0.05, (jetzt - vorher) / 1000);
            vorher = jetzt;
            const gleiten = federn();
            let ruhig = z.lagen === ziel;
            // kleine Teilschritte halten die Federn auch nach Rucklern stabil
            while (dt > 1e-6) {
                const h = Math.min(dt, 1 / 120);
                dt -= h;
                for (const f of gleiten) ruhig = schritt(f, h, LAGE) && ruhig;
                tropfenZiel();
                ruhig = schritt(z.kopf, h, KOPF) && ruhig;
                ruhig = schritt(z.schwanz, h, SCHWANZ) && ruhig;
                if (Math.abs(z.schwanz.x - z.kopf.x) > maxZug) {
                    z.schwanz.x = z.kopf.x + Math.sign(z.schwanz.x - z.kopf.x) * maxZug;
                    z.schwanz.v = z.kopf.v;
                }
            }
            neuZeichnen((n) => n + 1);
            if (!ruhig) id = requestAnimationFrame(takt);
        };
        id = requestAnimationFrame(takt);
        return () => cancelAnimationFrame(id);
    }, [ziel, aktiv, masse]);

    return zustand.current;
}

/** Unsichtbare Klickfläche einer Zeile (ganze Pillenbreite, bis zur Hälfte des Abstands). */
function Punkt({
    top,
    hoehe,
    breite,
    label,
    aktiv,
    beschriftung,
    zeigen,
    farbe,
    grund,
    onUeber,
    onClick,
}: {
    top: number;
    hoehe: number;
    breite: number;
    label: string;
    aktiv: boolean;
    /** sichtbares Schild links beim Darüberfahren (Kapitelname) */
    beschriftung?: string;
    zeigen: boolean;
    farbe: string;
    grund: string;
    onUeber: (an: boolean) => void;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            aria-label={label}
            aria-current={aktiv ? "step" : undefined}
            onClick={onClick}
            onMouseEnter={() => onUeber(true)}
            onMouseLeave={() => onUeber(false)}
            onFocus={() => onUeber(true)}
            onBlur={() => onUeber(false)}
            style={{
                position: "absolute",
                left: 0,
                top,
                width: breite,
                height: hoehe,
                padding: 0,
                margin: 0,
                border: 0,
                background: "transparent",
                cursor: "pointer",
            }}
        >
            {beschriftung && zeigen && (
                <span
                    style={{
                        position: "absolute",
                        right: "calc(100% + 8px)",
                        top: "50%",
                        transform: "translateY(-50%)",
                        whiteSpace: "nowrap",
                        padding: "5px 9px",
                        borderRadius: 6,
                        background: grund,
                        color: farbe,
                        font: "600 12px/1.2 system-ui, sans-serif",
                        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.25)",
                        pointerEvents: "none",
                    }}
                >
                    {beschriftung}
                </span>
            )}
        </button>
    );
}

export function KapitelPunkte({
    slides,
    aktuellerSlide,
    store,
    farbe,
    grund,
    maxHoehe,
    kapitelWort = "Kapitel",
}: {
    slides: ReadonlyArray<KapitelSlide | null | undefined>;
    aktuellerSlide: number;
    store: VisualStoryStore;
    /** Punkte und Tropfen (= Pfeilfarbe der Nav-Knöpfe) */
    farbe: string;
    /** Fläche der Pille (= Hintergrund der Nav-Knöpfe) */
    grund: string;
    /** Platz, ohne die Nav-Knöpfe unten zu berühren */
    maxHoehe: number;
    /** Standard-Bezeichnung ohne „Name am Punkt“, in der Sprache des Lesers */
    kapitelWort?: string;
}) {
    const phase = useStoryPhase(store);
    const gruppen = useMemo(() => kapitelGruppen(slides), [slides]);
    const masse = passendeMasse(gruppen, maxHoehe);
    const offen = kapitelPosition(gruppen, aktuellerSlide)?.kapitel ?? -1;
    const ziel = useMemo(() => (masse ? punktLagen(gruppen, offen, masse) : null), [gruppen, offen, masse]);
    const aktiv = ziel?.punkte.find((p) => p.slide === aktuellerSlide && p.r > 0) ?? null;
    const z = useSchlonzen(ziel, aktiv, masse);
    const [ueber, setUeber] = useState<number | null>(null);
    // Ein einzelner Punkt hilft niemandem — und Titel + EINE Slide (z. B.
    // Echtzeit-Scrollys) brauchen keine Leiste; passt sie nicht, bleibt sie weg
    if (!masse || !ziel || !z || gruppen.length < 2 || slides.length < 3) return null;

    const sichtbar = phase !== "vor";
    const breite = masse.gross + 2 * masse.rand;
    const mitteX = breite / 2;
    const reichweite = masse.abstand * REICHWEITE;

    // Die Punkte, wie sie gerade stehen (während einer Bewegung zwischen zwei Lagen)
    const kreise = z.lagen.punkte.map((p) => {
        const f = z.punkte.get(p.slide);
        return { lage: p, x: mitteX, y: f ? f.y.x : p.y, r: Math.max(0, f ? f.r.x : p.r) };
    });
    // Hälse zwischen Nachbarn im selben Kapitel — nah genug nur beim Auf- und Zuklappen
    let punktHaelse = "";
    for (let i = 1; i < kreise.length; i++) {
        if (kreise[i].lage.kapitel === kreise[i - 1].lage.kapitel) {
            punktHaelse += hals(kreise[i - 1], kreise[i], reichweite);
        }
    }

    // Der Tropfen: Kopf voraus, Schwanz hinterher; gestreckt wird er schmaler
    const rTropfen = Math.max(0, z.tropfen.x);
    const streckung = rTropfen > 0 ? Math.min(1, Math.abs(z.kopf.x - z.schwanz.x) / (2 * rTropfen)) : 0;
    const kopf: Kreis = { x: mitteX, y: z.kopf.x, r: rTropfen * (1 - 0.1 * streckung) };
    const schwanz: Kreis = { x: mitteX, y: z.schwanz.x, r: rTropfen * (1 - 0.35 * streckung) };
    let tropfenHaelse = "";
    if (rTropfen > 0.3) {
        tropfenHaelse = hals(kopf, schwanz, Infinity, 0.6);
        for (const k of kreise) {
            if (k.r > 0.3) tropfenHaelse += hals(kopf, k, reichweite) + hals(schwanz, k, reichweite);
        }
    }
    const ueberKreis = kreise.find((k) => k.lage.slide === ueber && k.r > 0);
    const hoehe = z.hoehe.x;

    return (
        <nav
            aria-label="Kapitel"
            data-testid="kapitel-punkte"
            style={{
                // Nullhoch auf der Mittellinie; alles darin sitzt relativ zur Mitte
                position: "absolute",
                right: MITTE_VON_RECHTS - breite / 2,
                top: "50%",
                width: breite,
                height: 0,
                opacity: sichtbar ? 1 : 0,
                pointerEvents: sichtbar ? "auto" : "none",
                transition: "opacity 0.3s ease",
            }}
        >
            {/* Pille und Punkte in EINEM SVG mit echten Maßen (Jans Fund 07.10.2026):
                Vorher lag die Pille als eigenes Element mit backdrop-filter darunter,
                die Punkte in einem 1 px hohen SVG mit überstehendem Inhalt — Safari
                legte die Punkte UNTER den Weichzeichner und blurte sie mit. Im SVG
                ist die Reihenfolge fest: Pille unten, Punkte oben, ohne Weichzeichner. */}
            <svg
                aria-hidden
                width={breite}
                height={hoehe + 2 * UEBERSTAND}
                viewBox={`0 ${-hoehe / 2 - UEBERSTAND} ${breite} ${hoehe + 2 * UEBERSTAND}`}
                style={{
                    position: "absolute",
                    left: 0,
                    top: -hoehe / 2 - UEBERSTAND,
                    overflow: "visible",
                    pointerEvents: "none",
                }}
            >
                <rect x={0} y={-hoehe / 2} width={breite} height={hoehe} rx={breite / 2} fill={grund} fillOpacity={PILLE} />
                <g fill={farbe}>
                    <g opacity={BLASS}>
                        {kreise.map(
                            (k) =>
                                k.r > 0.05 &&
                                k !== ueberKreis && <circle key={k.lage.slide} cx={k.x} cy={k.y} r={k.r} />,
                        )}
                        {punktHaelse && <path d={punktHaelse} />}
                    </g>
                    {ueberKreis && <circle cx={ueberKreis.x} cy={ueberKreis.y} r={ueberKreis.r * 1.25} />}
                    {rTropfen > 0.05 && (
                        <>
                            {tropfenHaelse && <path d={tropfenHaelse} />}
                            <circle cx={schwanz.x} cy={schwanz.y} r={schwanz.r} />
                            <circle cx={kopf.x} cy={kopf.y} r={kopf.r} />
                        </>
                    )}
                </g>
            </svg>
            {kreise
                .filter((k) => k.lage.r > 0)
                .map(({ lage: p, y }) => {
                    const g = gruppen[p.kapitel];
                    // NUR EIN KAPITEL (Jan 20.10.2026: „sollte auch ohne ein
                    // 2. Kapitel am Dot beschriftet sein“): ein Punkt je Slide,
                    // aber der Titel-Punkt trägt den Kapitelnamen, wenn einer
                    // eingetragen ist (ohne Namen kein Schild).
                    const ersterName = !g.istKapitel && p.slide === 0 ? kapitelName(slides, 0) : undefined;
                    const kapitelLabel = g.istKapitel
                        ? kapitelName(slides, g.slides[0]) || `${kapitelWort} ${p.kapitel + 1}`
                        : ersterName || `Slide ${p.kapitel + 1}`;
                    const halb = p.r + masse.abstand / 2;
                    return (
                        <Punkt
                            key={p.slide}
                            top={y - halb}
                            hoehe={2 * halb}
                            breite={breite}
                            label={p.nr === 0 ? kapitelLabel : `${kapitelLabel}, Slide ${p.nr + 1}`}
                            aktiv={aktiv?.slide === p.slide}
                            beschriftung={(g.istKapitel && p.nr === 0) || ersterName ? kapitelLabel : undefined}
                            zeigen={ueber === p.slide}
                            farbe={farbe}
                            grund={grund}
                            onUeber={(an) => setUeber((alt) => (an ? p.slide : alt === p.slide ? null : alt))}
                            // im eigenen Kapitel läuft der Scrolly durch, sonst darf er schneiden
                            onClick={() => store.springeZuSlide(p.slide, p.kapitel === offen)}
                        />
                    );
                })}
        </nav>
    );
}
